<script setup>
// 中獎名單：對應設計稿「中獎名單」。
//
// 兩態共用一支路由，切換的依據就是後端的 announced：
//   未公布（活動進行中／即將開始）→ 禮物卡印公告日期 + 說明文字
//   已公布（最近一檔已結束且過了公佈日）→ 黃底大框內一個獎項一張卡的得獎名單 + 領獎提醒
// 設計稿把這兩態畫成兩張圖，但它們是同一頁的兩個狀態，不是兩支路由。
//
// 資料取自 /front/winner（公開介面，SSR 期間就取回）。名單可能早就抽好了，
// 但要等公佈日期到了後端才回 announced=true，前台不必自己判斷日期。
//
// 這一頁刻意不像活動辦法那樣退回設計稿的假資料——公告日期與中獎者
// 都是事實陳述，塞假日期假名字會誤導使用者（與步道排行榜同一個理由）。
import bannerBg from '~/assets/img/winner-banner-bg.png'
import titleCard from '~/assets/img/winner-title-card.png'
import giftBoard from '~/assets/img/winner-gift-board.png'
import dateTag from '~/assets/img/winner-date-tag.png'
import boardTab from '~/assets/img/winner-board-tab.png'
import boardScallop from '~/assets/img/winner-board-scallop.png'
import boardDots from '~/assets/img/winner-board-dots.png'

definePageMeta({ layout: false })

const config = useRuntimeConfig()
useHead({ title: config.public.siteName })

const api = useApi()
const { resolveFileUrl } = useFileUrl()

const { data, pending } = await useAsyncData('front-winner', () =>
  api.get('/front/winner').catch(() => null)
)

const activity = computed(() => data.value?.activity || null)
const announced = computed(() => Boolean(data.value?.announced))

/* ---------------- 日期 ---------------- */

// 全頁的日期一律民國年。設計稿的禮物卡印的是西元（2026.10.29），
// 但這是政府活動的公告，年份跟著說明文字一起用民國年才不會一頁兩套曆
function rocDate(date) {
  const [y, m, d] = String(date || '')
    .slice(0, 10)
    .split('-')
  return y && m && d ? `${Number(y) - 1911}.${Number(m)}.${Number(d)}` : ''
}

const announceText = computed(() => rocDate(activity.value?.announce_date) || '待公布')
// 公佈時間精確到分（5.20），時分另起一行小字，日期那行的字級才不會撐破禮物卡
const announceTime = computed(() => String(activity.value?.announce_date || '').slice(11, 16))

// 設計稿的說明文字。截止日取自活動，取不到就整句換掉——寧可少一段，也不要印錯的日期。
// 稿上寫的是「並於下方日期公布」，但公告日期的禮物卡排在這段文字的上面，照抄會指錯方向
const noticeLead = computed(() => {
  const end = rocDate(activity.value?.end_date)
  return end
    ? `本活動網路票選將於 ${end} 23:59 截止，主辦單位將進行抽獎作業並於上方日期公布得獎名單，`
    : '本活動網路票選截止後，主辦單位將進行抽獎作業並於上方日期公布得獎名單，'
})

/* ---------------- 名單 ---------------- */

const groups = computed(() =>
  (data.value?.groups || []).map((group) => {
    const winners = (group.winners || []).map((w) => ({
      id: w.id,
      name: w.name,
      memberId: w.member_id
    }))
    // 獎品名稱底下印「(共2台)」。數量與單位取自獎品清單，獎品被刪掉就
    // 退回中獎人數——抽獎本來就是把數量展開成獎位，兩個數字對得起來
    const quantity = Number(group.quantity || 0) || winners.length
    const unit = group.unit || '名'
    return {
      key: `${group.prize_id ?? ''}-${group.prize_level}-${group.prize_name}`,
      level: group.prize_level,
      name: group.prize_name,
      count: quantity ? `(共${quantity}${unit})` : '',
      image: resolveFileUrl(group.image),
      winners
    }
  })
)

// 獎品圖是後台上傳的，檔案被清掉時 <img> 只會留下一個 90px 的空洞，
// 載不動就換成禮物圖示，版位才不會開天窗
const brokenPics = reactive({})

// SSR 送來的 <img> 有機會在 hydration 之前就載失敗，那一顆 error 事件沒人接得到，
// 元素掛上來時照 naturalWidth 再補判一次
function checkPic(el, key) {
  if (el && el.complete && !el.naturalWidth) brokenPics[key] = true
}

/* ---------------- 揪團投票禮 ---------------- */

// 不參與抽獎、由邀請排行榜前 N 名獲得的那一項（每檔活動最多一項，5.20）。
// 得獎者動輒幾百人，設計稿只印「名次 + 會員 ID」，後端也只回這兩個欄位
const groupGift = computed(() => {
  const gift = data.value?.group_gift
  if (!gift) return null
  const quantity = Number(gift.quantity || 0)
  const winners = gift.winners || []
  // 名次補零到與名額同樣的位數（設計稿 500 名 → 001），至少三位
  const width = Math.max(3, String(quantity || winners.length).length)
  return {
    level: gift.prize_level,
    name: gift.prize_name,
    quantity,
    count: quantity ? `(共${quantity}${gift.unit || '份'})` : '',
    image: resolveFileUrl(gift.image),
    winners: winners.map((w) => ({
      key: `${w.rank}-${w.member_id}`,
      rank: String(w.rank).padStart(width, '0'),
      memberId: w.member_id
    }))
  }
})

/* ---------------- 勤投加碼禮 ---------------- */

// 投票排行榜第 1–300 名分三段，第 301 名後抽 5 位（5.20 的 vote_rank_gift）。
// 名次段與抽獎名額由後端給，名單只印會員 ID（設計稿 assets/img/demo/勤投加碼禮.png）；
// 投票人數不夠時後面的段落是空的，整段不畫
const voteRankGift = computed(() => {
  const gift = data.value?.vote_rank_gift
  if (!gift) return null
  const tiers = (gift.tiers || [])
    .filter((tier) => tier.winners?.length)
    .map((tier) => ({
      key: `${tier.from}-${tier.to}`,
      label: `第${tier.from}–${tier.to}名`,
      winners: tier.winners.map((w) => ({ key: `${w.rank}-${w.member_id}`, memberId: w.member_id }))
    }))
  const lucky = gift.lucky || {}
  const top = gift.tiers?.length ? gift.tiers[gift.tiers.length - 1].to : 0
  return {
    top,
    tiers,
    lucky: {
      after: Number(lucky.after || top),
      count: Number(lucky.count || 0),
      winners: (lucky.winners || []).map((w) => w.member_id)
    }
  }
})

/* ---------------- 領獎提醒 ---------------- */

// 後台活動管理的「領獎提醒」富文本（claim_content）。既有活動沒填時退回原本寫死的這一段，
// 判斷空白的口徑同活動辦法的 richOr：編輯器清空後會留下 <p><br></p> 這類殘骸
const FALLBACK_CLAIM =
  '<p>得獎通知將個別發送至您登入使用的 Google 或 LINE 帳號信箱，請留意查收。<br />' +
  '如有疑問請聯繫活動小組：02-2758-6688 分機26（週一至週五 10:00-12:00、13:30-18:00）</p>'

const claimHtml = computed(() => {
  const raw = String(activity.value?.claim_content || '')
  const text = raw
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return text || /<img\b/i.test(raw) ? raw : FALLBACK_CLAIM
})

// 已公布卻一位得獎者都沒有（獎品清單是空的）時，版面仍要有交代
const emptyText = computed(() => (pending.value ? '載入中…' : '本次活動沒有中獎名單'))
</script>

<template>
  <div class="win">
    <div class="head-fix">
      <SiteHeader active="winner" />
    </div>

    <!-- 橫幅：底圖含左右兩位人物，中間留給標題牌 -->
    <section class="banner" :style="{ backgroundImage: `url(${bannerBg})` }">
      <div class="plate">
        <img class="plate-img" :src="titleCard" alt="" aria-hidden="true" />
        <h1 class="plate-title">中獎名單</h1>
        <p class="plate-sub">— 抽獎結果公告 —</p>
      </div>
    </section>

    <main class="body">
      <div class="wrap">
        <!-- 尚未公布：設計稿的禮物卡那一態，卡上印公告日期 -->
        <template v-if="!announced">
          <!-- 禮物卡是一張切圖（含蝴蝶結與下緣的橘色區塊），
               內容只壓在中間的白色區域上，位置以圖內的白區為準 -->
          <section class="board" :style="{ backgroundImage: `url(${giftBoard})` }">
            <div class="board-inner">
              <span class="tag" :style="{ backgroundImage: `url(${dateTag})` }">公告日期</span>
              <p class="board-date">{{ announceText }}</p>
              <p v-if="announceTime" class="board-time">{{ announceTime }} 公布</p>
            </div>
          </section>

          <section class="notice">
            <h2 class="notice-title">中獎名單尚未公布</h2>
            <p class="notice-text">
              {{ noticeLead }}<br />
              同時將寄發中獎通知至您登入使用的 Google 或 LINE 帳號信箱，請留意查收。
            </p>
          </section>
        </template>

        <!-- 已公布：黃底大框裡一個獎項一張白卡，左邊獎品、右邊得獎者
             （名字後面接會員 ID，遮罩後同名的人才分得出來） -->
        <template v-else>
          <section class="ticket" :style="{ backgroundImage: `url(${boardDots})` }">
            <img class="ticket-tab" :src="boardTab" alt="" aria-hidden="true" />
            <div class="ticket-inner">
              <img class="ticket-edge" :src="boardScallop" alt="" aria-hidden="true" />
              <div v-if="groups.length" class="ticket-body">
                <p class="gift-list-tip ticket-tip">會員 ID 可至「我的帳號 › 個人資料」查看。</p>
                <article v-for="group in groups" :key="group.key" class="lot">
                  <span class="lot-tab">{{ group.level }}</span>

                  <div class="lot-prize">
                    <span class="lot-pic">
                      <img
                        v-if="group.image && !brokenPics[group.key]"
                        :src="group.image"
                        :ref="(el) => checkPic(el, group.key)"
                        :alt="group.name"
                        loading="lazy"
                        @error="brokenPics[group.key] = true"
                      />
                      <span v-else class="lot-pic-empty" aria-hidden="true">🎁</span>
                    </span>
                    <h2 class="lot-name">{{ group.name }}</h2>
                    <p v-if="group.count" class="lot-count">{{ group.count }}</p>
                  </div>

                  <div class="lot-winners">
                    <p class="lot-label">中獎者</p>
                    <ul class="lot-names">
                      <li v-for="winner in group.winners" :key="winner.id" class="lot-chip">
                        <span class="lot-chip-name">{{ winner.name }}</span>
                        <span v-if="winner.memberId" class="lot-chip-id"
                          >({{ winner.memberId }})</span
                        >
                      </li>
                    </ul>
                  </div>
                </article>
              </div>

              <p v-else class="ticket-empty">{{ emptyText }}</p>
              <img class="ticket-edge is-foot" :src="boardScallop" alt="" aria-hidden="true" />
            </div>
          </section>

          <!-- 揪團投票禮：依設計稿「揪團投票禮」，同樣是黃底大框，
               上面一張獎品＋獲獎資格說明，下面一張只印名次與會員 ID 的名單 -->
          <section
            v-if="groupGift"
            class="ticket gift"
            :style="{ backgroundImage: `url(${boardDots})` }"
          >
            <img class="ticket-tab" :src="boardTab" alt="" aria-hidden="true" />
            <div class="ticket-inner">
              <img class="ticket-edge" :src="boardScallop" alt="" aria-hidden="true" />
              <div class="ticket-body">
                <header class="gift-head">
                  <h2 class="gift-title">揪團投票禮</h2>
                  <p v-if="groupGift.quantity" class="gift-sub">
                    前 {{ groupGift.quantity }} 名成功邀請者
                  </p>
                </header>

                <article class="lot">
                  <span v-if="groupGift.level" class="lot-tab">{{ groupGift.level }}</span>

                  <div class="lot-prize">
                    <span class="lot-pic">
                      <img
                        v-if="groupGift.image && !brokenPics.gift"
                        :src="groupGift.image"
                        :ref="(el) => checkPic(el, 'gift')"
                        :alt="groupGift.name"
                        loading="lazy"
                        @error="brokenPics.gift = true"
                      />
                      <span v-else class="lot-pic-empty" aria-hidden="true">🎁</span>
                    </span>
                    <h3 class="lot-name">{{ groupGift.name }}</h3>
                    <p v-if="groupGift.count" class="lot-count">{{ groupGift.count }}</p>
                  </div>

                  <div class="gift-rule">
                    <p class="lot-label">獲獎資格說明</p>
                    <p class="gift-text">
                      官方將依據「帳號資訊」中「任務進度」所顯示之「揪團投票禮」任務狀態為「已達成」的參與者名單進行資格審核。經確認符合活動資格者，將透過電子郵件方式發送<strong
                        >{{ groupGift.name }}乙份</strong
                      >。
                    </p>
                    <p class="gift-text">
                      若經查核發現有不符合資格之帳號，將依後續排名順序遞補，直至符合資格之前{{
                        groupGift.quantity
                      }}名獲獎者全數確認為止。
                    </p>
                  </div>
                </article>

                <article class="gift-list">
                  <div class="gift-list-head">
                    <h3 class="gift-list-title">
                      獲獎名單
                      <small>（共{{ groupGift.winners.length }}名，依排序）</small>
                    </h3>
                    <p class="gift-list-note">會員 ID 可至「我的帳號 › 個人資料」查看</p>
                  </div>
                  <p class="gift-list-tip">
                    因獲獎人數眾多，為維持頁面瀏覽順暢，名單僅以會員 ID 呈現。
                  </p>

                  <ul v-if="groupGift.winners.length" class="gift-grid">
                    <li v-for="winner in groupGift.winners" :key="winner.key" class="gift-chip">
                      <span class="gift-chip-rank">{{ winner.rank }}</span>
                      <span class="gift-chip-id">{{ winner.memberId }}</span>
                    </li>
                  </ul>
                  <p v-else class="gift-empty">本次活動沒有符合資格的邀請者</p>
                </article>
              </div>
              <img class="ticket-edge is-foot" :src="boardScallop" alt="" aria-hidden="true" />
            </div>
          </section>

          <!-- 勤投加碼禮：依設計稿「勤投加碼禮」，外框沿用同一張黃色票券，
               一個名次段一張卡，最後一張是第 301 名後抽出的幾位 -->
          <section
            v-if="voteRankGift"
            class="ticket gift"
            :style="{ backgroundImage: `url(${boardDots})` }"
          >
            <img class="ticket-tab" :src="boardTab" alt="" aria-hidden="true" />
            <div class="ticket-inner">
              <img class="ticket-edge" :src="boardScallop" alt="" aria-hidden="true" />
              <div class="ticket-body">
                <header class="gift-head">
                  <h2 class="gift-title">勤投加碼禮</h2>
                  <p class="gift-sub">
                    第 1–{{ voteRankGift.top }} 名依投票排行榜排名發放，第
                    {{ voteRankGift.lucky.after + 1 }} 名後之參與者另抽出
                    {{ voteRankGift.lucky.count }} 名
                  </p>
                  <p class="gift-list-tip">
                    名單以會員 ID 呈現；第 1–{{ voteRankGift.top }}
                    名依排名順序排列。會員 ID 可至「我的帳號 › 個人資料」查看。
                  </p>
                </header>

                <article v-for="tier in voteRankGift.tiers" :key="tier.key" class="rank-tier">
                  <span class="lot-tab">{{ tier.label }}</span>
                  <p class="lot-label">獲獎名單（共{{ tier.winners.length }}名，依排名順序）</p>
                  <ul class="gift-grid">
                    <li v-for="winner in tier.winners" :key="winner.key" class="gift-chip">
                      <span class="gift-chip-id">{{ winner.memberId }}</span>
                    </li>
                  </ul>
                </article>

                <article v-if="voteRankGift.lucky.winners.length" class="rank-tier">
                  <span class="lot-tab">第{{ voteRankGift.lucky.after + 1 }}名後抽獎</span>
                  <p class="lot-label rank-lucky-label">
                    第 {{ voteRankGift.lucky.after + 1 }} 名後之所有參與者抽出 500 元，共
                    {{ voteRankGift.lucky.winners.length }} 名
                  </p>
                  <ul class="gift-grid">
                    <li
                      v-for="memberId in voteRankGift.lucky.winners"
                      :key="memberId"
                      class="gift-chip"
                    >
                      <span class="gift-chip-id">{{ memberId }}</span>
                    </li>
                  </ul>
                </article>
              </div>
              <img class="ticket-edge is-foot" :src="boardScallop" alt="" aria-hidden="true" />
            </div>
          </section>

          <!-- 領獎提醒：黑色標籤壓在綠色漸層卡的上緣，卡內再包一層白底 -->
          <section class="remind">
            <span class="remind-tag">領獎提醒</span>
            <div class="remind-card">
              <!-- 內容是後台富文本（claim_content），標籤由編輯器決定，樣式在 .remind-text 底下收斂 -->
              <div class="remind-text" v-html="claimHtml" />
            </div>
          </section>
        </template>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.win {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 頁首釘在畫面最上方，內容捲動時不跟著走（與其他內頁一致） */
.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

/* ---------------- 橫幅 ---------------- */

/* 設計稿是 1440 × 300 的底圖，人物在左右兩側，窄螢幕時從兩邊往內裁 */
.banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1440 / 300;
  min-height: 300px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
}

/* 標題牌：切圖含外框與大聲公，文字壓在圖上。
   兩行的位置以圖內的橘色橫條與下方白區為準（百分比是量圖來的，換圖要一起改） */
.plate {
  position: relative;
  width: 446px;
  max-width: 62%;
  aspect-ratio: 446 / 275;
}

.plate-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.plate-title,
.plate-sub {
  position: absolute;
  left: 8%;
  width: 82%;
  margin: 0;
  text-align: center;
  line-height: 1;
  transform: translateY(-50%);
}

/* 橘色橫條的中線 */
.plate-title {
  top: 38.9%;
  color: #fff;
  font-size: clamp(44px, 4.1vw, 60px);
  font-weight: 900;
  letter-spacing: 4px;
  text-shadow: 0 2px 0 rgba(var(--site-shadow-rgb), 0.18);
}

/* 橫條下方白區的中線 */
.plate-sub {
  top: 71.5%;
  color: #000000;
  font-size: clamp(14px, 1.4vw, 20px);
  font-weight: 500;
  letter-spacing: 2px;
}

/* ---------------- 內容 ---------------- */

.body {
  position: relative;
  flex: 1;
  /* 底部要留給頁尾的山稜線疊上來：稜線是 1440 × 175 等比縮放，
     窄螢幕上只有幾十 px，用固定值會留下一大片空白（與山林問答挑戰同一套） */
  padding: 50px 0 calc(min(12.15vw, 175px) + 60px);
}

.wrap {
  max-width: var(--site-max);
  margin: 0 auto;
  padding: 0 20px;
}

/* ---------------- 尚未公布：禮物卡 ---------------- */

.board {
  position: relative;
  width: 376px;
  max-width: 100%;
  margin: 0 auto;
  aspect-ratio: 376 / 415;
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
}

.board-inner {
  position: absolute;
  left: 12%;
  right: 12%;
  top: 21.7%;
  bottom: 31.3%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* 黑色氣泡標籤：切圖下緣是尖角，文字要往上讓開，否則會壓在尖角上 */
.tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 137px;
  height: 49px;
  padding-bottom: 11px;
  background-position: center center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  color: #fff;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: 1px;
}

/* 民國年比設計稿的西元寫法多了「年月日」三個中文字，字級要跟著收，
   否則會撐破禮物卡的白色區域 */
.board-date {
  margin: 0;
  color: #010101;
  font-size: clamp(40px, 4vw, 61px);
  font-weight: 900;
  line-height: 1.1;
  white-space: nowrap;
  text-align: center;
}

.board-time {
  margin: 6px 0 0;
  color: #010101;
  font-size: clamp(16px, 1.6vw, 22px);
  font-weight: 700;
  text-align: center;
}

.notice {
  margin-top: 30px;
  text-align: center;
}

.notice-title {
  margin: 0;
  color: #02050a;
  font-size: clamp(20px, 2vw, 24px);
  font-weight: 600;
  line-height: 1.2;
}

.notice-text {
  margin: 14px 0 0;
  color: #8f8f8f;
  font-size: 14px;
  line-height: 1.4;
}

/* ---------------- 已公布：得獎名單 ---------------- */

/* 黃色票券：與領獎提醒同寬，左右留 30、上下留 60 的黃邊，下緣的點點紋是一張切圖，
   寬度跟著票券縮放（原圖 900 就是票券的設計寬度）。白色內頁裡一個獎項一張卡 */
.ticket {
  position: relative;
  width: 900px;
  max-width: 100%;
  margin: 0 auto;
  padding: 60px 30px;
  border: 1px solid #f4ca0e;
  border-radius: 28px;
  background-color: #ffec65;
  background-position: center bottom;
  background-size: 100% auto;
  background-repeat: no-repeat;
}

/* 票券頂端的掛耳，壓在上緣的黃邊上 */
.ticket-tab {
  position: absolute;
  left: 50%;
  top: 10px;
  width: 84px;
  transform: translateX(-50%);
  user-select: none;
  pointer-events: none;
}

.ticket-inner {
  position: relative;
}

/* 內頁的白底只鋪在中段：上下兩條波浪邊是「白底挖出一排半圓缺口」的切圖，
   缺口要透出底下票券的黃色，這裡要是也上白底就把缺口填平了 */
.ticket-body {
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 20px 24px 8px;
  background: #fff;
}

/* 波浪邊：切圖連白色內頁的圓角一起帶了，下緣同一張圖翻過來用 */
.ticket-edge {
  display: block;
  width: 100%;
  height: 40px;
  user-select: none;
  pointer-events: none;
}

.ticket-edge.is-foot {
  transform: scaleY(-1);
}

/* 獎項卡：左欄固定給獎品，右欄的得獎者再多也只會往下長 */
.lot {
  position: relative;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 28px;
  padding: 34px 28px 28px;
  border: 2px solid #f6c945;
  border-radius: 18px;
  background: #fff;
}

/* 獎項標籤：橘色小牌掛在卡片左上緣，一半壓出卡外 */
.lot-tab {
  position: absolute;
  left: 18px;
  top: 0;
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 14px;
  border-radius: 8px 8px 8px 2px;
  background: var(--site-orange);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  transform: translateY(-50%);
  box-shadow: 0 2px 0 rgba(var(--site-shadow-rgb), 0.15);
}

.lot-prize {
  text-align: center;
}

.lot-pic {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4 / 3;
  padding: 14px;
  border: 1px solid #f8e4ad;
  border-radius: 14px;
  background: #fffaeb;
  overflow: hidden;
}

.lot-pic img {
  display: block;
  width: 100%;
  height: 100%;
  /* 獎品圖多半是去背的商品照，contain 才不會把邊角裁掉 */
  object-fit: contain;
}

.lot-pic-empty {
  font-size: 48px;
  line-height: 1;
}

.lot-name {
  margin: 14px 0 0;
  color: #1b1b1b;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.45;
}

.lot-count {
  margin: 4px 0 0;
  color: var(--site-text-weak);
  font-size: 13px;
  line-height: 1.4;
}

.lot-label {
  margin: 0;
  padding-bottom: 10px;
  color: #505153;
  font-size: 14px;
  font-weight: 700;
  /* 虛線用漸層自己畫：CSS 的 dashed 間距各家瀏覽器畫得不一樣 */
  background-image: repeating-linear-gradient(90deg, #f6c945 0 6px, transparent 6px 10px);
  background-position: bottom left;
  background-size: 100% 1px;
  background-repeat: no-repeat;
}

/* 得獎者一列三格，名字長短不一時上下列才對得齊 */
.lot-names {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.lot-chip {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 10px 8px;
  border: 1px solid #f3c86a;
  border-radius: 10px;
  background: #fff8e6;
  color: #ea5620;
  text-align: center;
}

.lot-chip-name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  word-break: keep-all;
}

.lot-chip-id {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
}

.ticket-empty {
  margin: 0;
  padding: 60px 20px;
  background: #fff;
  color: var(--site-text-weak);
  font-size: 15px;
  text-align: center;
}

/* ---------------- 揪團投票禮 ---------------- */

.gift {
  margin-top: 52px;
}

/* 標題、獎品卡與名單之間的間距交給 .ticket-body 的 gap */
.gift-head {
  margin-bottom: 0;
}

/* 標題左側的橘色短豎線 */
.gift-title {
  margin: 0;
  padding-left: 16px;
  border-left: 3px solid var(--site-orange);
  color: #1b1b1b;
  font-size: clamp(24px, 2.4vw, 32px);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 1px;
}

.gift-sub {
  margin: 10px 0 0;
  color: #505153;
  font-size: 14px;
}

.gift-rule {
  min-width: 0;
}

.gift-text {
  margin: 18px 0 0;
  color: #1b1b1b;
  font-size: 15px;
  line-height: 1.9;
}

.gift-text strong {
  color: #ea5620;
  font-weight: 700;
}

.gift-list {
  padding: 26px 28px 28px;
  border: 2px solid #f6c945;
  border-radius: 18px;
  background: #fff;
}

.gift-list-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.gift-list-title {
  margin: 0;
  color: #1b1b1b;
  font-size: 20px;
  font-weight: 900;
}

.gift-list-title small {
  margin-left: 6px;
  color: #505153;
  font-size: 14px;
  font-weight: 500;
}

.gift-list-note {
  margin: 0;
  color: #505153;
  font-size: 13px;
}

.gift-list-tip {
  margin: 16px 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fdf3d6;
  color: #505153;
  font-size: 13px;
}

/* 抽獎名單最上面那一行：與獎項卡的間距交給 .ticket-body 的 gap */
.ticket-tip {
  margin: 0;
}

/* 名單幾百格，框內自己捲動，整頁才不會被拉得老長 */
.gift-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  max-height: 400px;
  margin: 14px 0 0;
  padding: 14px;
  border: 1px solid #f8e4ad;
  border-radius: 12px;
  list-style: none;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.gift-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 6px;
  border: 1px solid #f3c86a;
  border-radius: 10px;
  background: #fff8e6;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.gift-chip-rank {
  color: #505153;
}

.gift-chip-id {
  color: #ea5620;
}

.gift-empty {
  margin: 14px 0 0;
  padding: 40px 20px;
  color: var(--site-text-weak);
  font-size: 15px;
  text-align: center;
}

/* ---------------- 勤投加碼禮 ---------------- */

/* 名次段卡：外觀同獎項卡，橘色小牌一樣掛在左上緣，只是內容只有一張名單 */
.rank-tier {
  position: relative;
  padding: 34px 28px 28px;
  border: 2px solid #f6c945;
  border-radius: 18px;
  background: #fff;
}

.rank-lucky-label {
  color: #1b1b1b;
  font-size: 16px;
}

/* ---------------- 領獎提醒 ---------------- */

.remind {
  width: 900px;
  max-width: 100%;
  margin: 52px auto 0;
}

/* 黑色標籤坐在綠色卡片的上緣，下緣切齊、只圓上面兩角 */
.remind-tag {
  display: inline-flex;
  align-items: center;
  height: 48px;
  margin-left: 50px;
  padding: 0 18px;
  border-radius: 10px 10px 0 0;
  background: #000000;
  color: #fffc47;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 1px;
}

.remind-card {
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(180deg, #f8f2a1 0%, #b5fa73 100%);
}

.remind-text {
  margin: 0;
  padding: 30px 24px;
  border-radius: 16px;
  background: #fff;
  color: #000000;
  font-size: 16px;
  line-height: 1.5;
}

.remind-text :deep(p),
.remind-text :deep(ol),
.remind-text :deep(ul) {
  margin: 0 0 10px;
}

.remind-text :deep(ol),
.remind-text :deep(ul) {
  padding-left: 24px;
}

.remind-text :deep(> :last-child) {
  margin-bottom: 0;
}

.remind-text :deep(h2),
.remind-text :deep(h3) {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
}

.remind-text :deep(a) {
  color: var(--site-orange-deep);
  text-decoration: underline;
}

.remind-text :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
}

.remind-text :deep(table) {
  width: 100%;
  margin: 0 0 10px;
  border-collapse: collapse;
}

.remind-text :deep(th),
.remind-text :deep(td) {
  padding: 8px 10px;
  border: 1px solid var(--site-line);
}

/* ---------------- 窄螢幕 ---------------- */

@media (max-width: 900px) {
  .banner {
    aspect-ratio: auto;
    min-height: 220px;
    padding: 30px 16px;
  }

  /* 底圖兩側的人物在窄螢幕會被裁掉大半，標題牌讓出的寬度改給文字 */
  .plate {
    width: 380px;
    max-width: 100%;
  }

  .ticket {
    padding: 44px 16px;
    border-radius: 20px;
  }

  .ticket-edge {
    height: 26px;
  }

  .ticket-body {
    padding: 16px 14px 6px;
  }

  .lot {
    grid-template-columns: 200px minmax(0, 1fr);
    gap: 20px;
    padding: 30px 20px 22px;
  }

  .lot-names {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gift-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .gift-list {
    padding: 22px 20px;
  }

  .rank-tier {
    padding: 30px 20px 22px;
  }

  .remind-text {
    font-size: 15px;
    line-height: 1.9;
  }
}

@media (max-width: 640px) {
  .banner {
    min-height: 180px;
  }

  .plate-title {
    letter-spacing: 2px;
  }

  .body {
    padding: 36px 0 56px;
  }

  .board {
    width: 300px;
  }

  .tag {
    width: 116px;
    height: 42px;
    padding-bottom: 9px;
    font-size: 14px;
  }

  .notice-text {
    font-size: 14px;
    line-height: 1.8;
  }

  .ticket-body {
    gap: 30px;
  }

  /* 手機收成一欄：獎品在上、得獎者在下 */
  .lot {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
    padding: 28px 14px 18px;
  }

  .lot-pic {
    width: 200px;
    max-width: 100%;
    margin: 0 auto;
  }

  .lot-chip-name {
    font-size: 16px;
  }

  .gift {
    margin-top: 36px;
  }

  .gift-list {
    padding: 18px 14px;
  }

  .rank-tier {
    padding: 28px 14px 18px;
  }

  .gift-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: 360px;
    padding: 10px;
  }

  .gift-chip {
    font-size: 14px;
  }

  .remind {
    margin-top: 36px;
  }

  .remind-tag {
    height: 40px;
    margin-left: 20px;
    font-size: 15px;
  }

  .remind-card {
    padding: 14px;
    border-radius: 18px;
  }

  .remind-text {
    padding: 16px 18px;
    font-size: 14px;
  }
}
</style>
